// src/components/settings/BotFlowsTab.tsx
//
// Settings → Bot Flows tab (PKT-0035 · Phase 1 MVP slice).
//
// What ships here:
//   - List view: cards for every Bot Flow the current user owns. Each card
//     shows name, channel, mode badge, active/paused state, "Open" button,
//     and a pause/activate toggle.
//   - Detail view: drills into one flow, shows the step list in flowchart
//     order (with the 2.5 branch step rendered between 2 and 3), the
//     escalate triggers panel (read-only this PR), and the mode toggle
//     (L2 / L3 / L4 radio buttons that PATCH the flow on change).
//
// Deferred to PKT-0035 Phase 2 (documented in scaffolding-research note):
//   - BotFlowTestPlayground (Test with sample DM · interactive)
//   - BotFlowRunsHistory (View Runs · drill into past conversations)
//   - Inline step editor (template dropdown, field checkboxes)

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
} from "@mui/icons-material";
import type {
  BotFlow,
  EscalateTriggers,
  FlowMode,
  FlowStep,
} from "../../types/botFlow";
import { FLOW_MODE_LABELS } from "../../types/botFlow";
import {
  botFlowService,
  isL4ModeLockedError,
} from "../../lib/botFlowService";
import BotModeChangeConfirmDialog from "./BotModeChangeConfirmDialog";

const CHANNEL_LABELS: Record<BotFlow["channel"], string> = {
  email: "Email",
  sms: "SMS",
  instagram_dm: "Instagram DM",
};

const MODE_CHIP_COLOR: Record<
  FlowMode,
  "default" | "info" | "warning" | "success"
> = {
  L2_draft_only: "info",
  L3_faq_auto: "warning",
  L4_full_auto: "success",
};

// --------------------------------------------------------------------------- //
// List view
// --------------------------------------------------------------------------- //

interface FlowListProps {
  flows: BotFlow[];
  onOpen: (flow: BotFlow) => void;
  onTogglePause: (flow: BotFlow) => void;
  busyFlowId: string | null;
}

const FlowList: React.FC<FlowListProps> = ({
  flows,
  onOpen,
  onTogglePause,
  busyFlowId,
}) => {
  if (flows.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No bot flows yet. Each new account gets a default "Instagram DM Intake"
        flow seeded automatically — if you're not seeing it, contact support.
      </Alert>
    );
  }
  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      {flows.map((flow) => (
        <Card key={flow.id} variant="outlined">
          <CardContent>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6" component="div">
                  {flow.name}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  <Chip
                    size="small"
                    label={CHANNEL_LABELS[flow.channel] ?? flow.channel}
                  />
                  <Chip
                    size="small"
                    color={MODE_CHIP_COLOR[flow.mode]}
                    label={flow.mode.replace(/_/g, " ")}
                  />
                  {!flow.is_active && (
                    <Chip size="small" color="default" label="Paused" />
                  )}
                </Stack>
                {flow.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {flow.description}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={flow.is_active}
                      onChange={() => onTogglePause(flow)}
                      disabled={busyFlowId === flow.id}
                    />
                  }
                  label={flow.is_active ? "Active" : "Paused"}
                  labelPlacement="start"
                />
              </Stack>
            </Stack>
          </CardContent>
          <CardActions>
            <Button size="small" onClick={() => onOpen(flow)}>
              Open
            </Button>
          </CardActions>
        </Card>
      ))}
    </Stack>
  );
};

// --------------------------------------------------------------------------- //
// Step list (read-only in MVP)
// --------------------------------------------------------------------------- //

function describeStep(step: FlowStep): string {
  switch (step.type) {
    case "send_template":
      return `Send template ${step.template_lookup.template_type}${
        step.wait_for_reply ? " · wait for reply" : ""
      }${step.timeout_hours ? ` · ${step.timeout_hours}h timeout` : ""}`;
    case "extract_fields":
      return `Extract: ${step.fields.join(", ")} · advance to ${
        step.on_complete["advance_to"]
      } if complete, ${step.on_missing_field["advance_to"]} if missing`;
    case "save_lead":
      return `Save lead from ${step.lead_fields.join(
        ", "
      )} · source=${step.source}`;
    case "notify_operator":
      return `Notify operator via ${step.via.join(" + ")}`;
    case "complete":
      return "Complete · close the run";
    default:
      // Exhaustiveness check (compile-time guard).
      return JSON.stringify(step);
  }
}

interface StepListProps {
  steps: FlowStep[];
}

const StepList: React.FC<StepListProps> = ({ steps }) => {
  const ordered = useMemo(
    () => [...steps].sort((a, b) => a.step_id - b.step_id),
    [steps]
  );
  return (
    <Stack spacing={1}>
      {ordered.map((step) => (
        <Paper
          key={`${step.step_id}-${step.type}`}
          variant="outlined"
          sx={{ p: 1.5 }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Chip
              size="small"
              label={`Step ${step.step_id}`}
              color={step.type === "complete" ? "success" : "default"}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                {step.type.replace(/_/g, " ")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {describeStep(step)}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

// --------------------------------------------------------------------------- //
// Escalate triggers panel (read-only in MVP)
// --------------------------------------------------------------------------- //

const EscalateTriggersPanel: React.FC<{ triggers: EscalateTriggers }> = ({
  triggers,
}) => {
  const rows: Array<{ label: string; value: string }> = [];
  if (triggers.urgent_move_date_days !== undefined) {
    rows.push({
      label: "Urgent move date",
      value: `≤ ${triggers.urgent_move_date_days} days out`,
    });
  }
  if (triggers.sensitive_keywords?.length) {
    rows.push({
      label: "Sensitive keywords",
      value: triggers.sensitive_keywords.join(" · "),
    });
  }
  if (triggers.tour_today_keywords?.length) {
    rows.push({
      label: "Tour-today keywords",
      value: triggers.tour_today_keywords.join(" · "),
    });
  }
  if (triggers.low_confidence_threshold !== undefined) {
    rows.push({
      label: "Min confidence",
      value: `${triggers.low_confidence_threshold}`,
    });
  }
  if (triggers.max_user_msgs_per_hour !== undefined) {
    rows.push({
      label: "Max msgs/hour from sender",
      value: `${triggers.max_user_msgs_per_hour}`,
    });
  }
  if (triggers.business_hours) {
    const bh = triggers.business_hours;
    rows.push({
      label: "Business hours",
      value: `${bh.start}–${bh.end} ${bh.timezone}`,
    });
  }
  if (rows.length === 0) {
    return (
      <Alert severity="info" variant="outlined">
        No escalation triggers configured.
      </Alert>
    );
  }
  return (
    <Stack spacing={1}>
      {rows.map((row) => (
        <Stack
          key={row.label}
          direction="row"
          spacing={2}
          alignItems="flex-start"
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 180 }}
          >
            {row.label}
          </Typography>
          <Typography variant="body2">{row.value}</Typography>
        </Stack>
      ))}
    </Stack>
  );
};

// --------------------------------------------------------------------------- //
// Detail view
// --------------------------------------------------------------------------- //

interface FlowDetailProps {
  flow: BotFlow;
  onBack: () => void;
  onModeChange: (next: FlowMode) => Promise<void>;
  saving: boolean;
}

const FlowDetail: React.FC<FlowDetailProps> = ({
  flow,
  onBack,
  onModeChange,
  saving,
}) => {
  const [mode, setMode] = useState<FlowMode>(flow.mode);
  const [pendingModeChange, setPendingModeChange] = useState<{
    fromMode: FlowMode;
    toMode: FlowMode;
  } | null>(null);

  useEffect(() => {
    setMode(flow.mode);
    setPendingModeChange(null);
  }, [flow.mode]);

  const commitModeChange = async (next: FlowMode, rollback: FlowMode) => {
    setMode(next);
    try {
      await onModeChange(next);
    } catch {
      // Roll back on failure; parent surfaces the error via snackbar.
      setMode(rollback);
    }
  };

  const handleModeChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const next = event.target.value as FlowMode;
    if (next === mode) return;

    if (next === "L3_faq_auto" || next === "L4_full_auto") {
      setPendingModeChange({ fromMode: mode, toMode: next });
      return;
    }

    await commitModeChange(next, mode);
  };

  const confirmPendingModeChange = async () => {
    if (!pendingModeChange || pendingModeChange.toMode === "L4_full_auto") return;
    const change = pendingModeChange;
    setPendingModeChange(null);
    await commitModeChange(change.toMode, change.fromMode);
  };

  const cancelPendingModeChange = () => {
    setPendingModeChange(null);
    if (pendingModeChange) setMode(pendingModeChange.fromMode);
  };

  return (
    <>
      <Stack spacing={3} sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={onBack} aria-label="Back to flow list">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">{flow.name}</Typography>
          <Chip
            size="small"
            label={CHANNEL_LABELS[flow.channel] ?? flow.channel}
          />
          <Chip
            size="small"
            color={MODE_CHIP_COLOR[flow.mode]}
            label={flow.mode.replace(/_/g, " ")}
          />
        </Stack>

        {flow.description && (
          <Typography variant="body2" color="text.secondary">
            {flow.description}
          </Typography>
        )}

        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Flow steps
          </Typography>
          <StepList steps={flow.steps} />
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Escalation triggers
          </Typography>
          <EscalateTriggersPanel triggers={flow.escalate_triggers} />
        </Box>

        <Box>
          <FormControl>
            <FormLabel id="bot-flow-mode-label">Mode</FormLabel>
            <RadioGroup
              aria-labelledby="bot-flow-mode-label"
              value={mode}
              onChange={handleModeChange}
            >
              {(Object.keys(FLOW_MODE_LABELS) as FlowMode[]).map((key) => (
                <FormControlLabel
                  key={key}
                  value={key}
                  control={<Radio disabled={saving} />}
                  label={FLOW_MODE_LABELS[key]}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {saving && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Saving mode change…
              </Typography>
            </Stack>
          )}
        </Box>

        <Divider />
        <Typography variant="caption" color="text.secondary">
          Test playground + run history coming in PKT-0035 Phase 2.
        </Typography>
      </Stack>

      {pendingModeChange && (
        <BotModeChangeConfirmDialog
          open={Boolean(pendingModeChange)}
          fromMode={pendingModeChange.fromMode}
          toMode={pendingModeChange.toMode}
          saving={saving}
          onCancel={cancelPendingModeChange}
          onConfirm={confirmPendingModeChange}
        />
      )}
    </>
  );
};

// --------------------------------------------------------------------------- //
// Top-level tab component
// --------------------------------------------------------------------------- //

const BotFlowsTab: React.FC = () => {
  const [flows, setFlows] = useState<BotFlow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busyFlowId, setBusyFlowId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const loadFlows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await botFlowService.listFlows({ includePaused: true });
      setFlows(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFlows();
  }, [loadFlows]);

  const selectedFlow = useMemo(
    () => flows?.find((f) => f.id === selectedId) ?? null,
    [flows, selectedId]
  );

  const handleTogglePause = useCallback(
    async (flow: BotFlow) => {
      setBusyFlowId(flow.id);
      try {
        const updated = flow.is_active
          ? await botFlowService.pauseFlow(flow.id)
          : await botFlowService.activateFlow(flow.id);
        setFlows((prev) =>
          prev ? prev.map((f) => (f.id === updated.id ? updated : f)) : prev
        );
        setSnackbar(
          updated.is_active ? `${updated.name} activated` : `${updated.name} paused`
        );
      } catch (err) {
        setSnackbar(
          `Could not toggle flow: ${
            err instanceof Error ? err.message : String(err)
          }`
        );
      } finally {
        setBusyFlowId(null);
      }
    },
    []
  );

  const handleModeChange = useCallback(
    async (next: FlowMode) => {
      if (!selectedFlow) return;
      setSaving(true);
      try {
        const updated = await botFlowService.patchFlow(selectedFlow.id, {
          mode: next,
        });
        setFlows((prev) =>
          prev ? prev.map((f) => (f.id === updated.id ? updated : f)) : prev
        );
        setSnackbar(`Mode set to ${next.replace(/_/g, " ")}`);
      } catch (err) {
        setSnackbar(
          isL4ModeLockedError(err)
            ? "L4 is code-locked. Use the engineering unlock checklist before enabling it."
            : `Could not save mode: ${
                err instanceof Error ? err.message : String(err)
              }`
        );
        throw err; // surface to FlowDetail so it can roll back the radio
      } finally {
        setSaving(false);
      }
    },
    [selectedFlow]
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Couldn't load bot flows: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Bot Flows orchestrate automated multi-step conversations. The default
        Instagram DM Intake flow handles renter inquiries — adjust the mode to
        control whether replies are drafted for review (L2), partially
        auto-sent (L3), or fully auto-sent (L4).
      </Typography>

      {selectedFlow ? (
        <FlowDetail
          flow={selectedFlow}
          onBack={() => setSelectedId(null)}
          onModeChange={handleModeChange}
          saving={saving}
        />
      ) : (
        <FlowList
          flows={flows ?? []}
          onOpen={(flow) => setSelectedId(flow.id)}
          onTogglePause={handleTogglePause}
          busyFlowId={busyFlowId}
        />
      )}

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar ?? ""}
      />
    </Box>
  );
};

export default BotFlowsTab;
